import { useState, useEffect, useRef } from "react";
import { ArrowLeft, Wrench, Mic, MicOff, Mail, Printer, Download, Save, X, Bookmark, Eye, ExternalLink, Package, Calculator, ChevronDown, FileCheck2, FileSearch, ArrowRightCircle, ClipboardCheck, Receipt } from "lucide-react";
import { Button, Badge } from "@/components/common";
import { HelpTip } from "@/components/HelpTip";

const EditorToolbar = ({
  type, isNew, titles, listPaths, docNumber, status, selectedCustomerId,
  isRecording, aiLoading, saving,
  navigate, setShowSettings, startRecording, stopRecording, handleBack,
  handleSave, handleExit, handleDownloadPDF, handlePrint,
  onOpenMailClient, onOpenBausteine, onTogglePreview,
  onOpenDocTemplates, onToggleLohnkosten, onToggleLeistungen, onOpenDocCheck, onOpenPdfPreview,
  zoomLevel, setZoomLevel,
  onNaechsterSchritt, copying,
  vorlageModus = false, // Vorlage bearbeiten: keine Beleg-Knöpfe (Vorschau/Nächster Schritt/Sprache/Vorlage öffnen)
  projektSchild = null, // A-015b: Projekt des Belegs, gut sichtbar neben der Überschrift
}) => {
  // Werkzeuge-Dropdown (Einstellungen, Vorlage, Bausteine, Vorschau)
  const [werkzeugeOffen, setWerkzeugeOffen] = useState(false);
  const werkzeugeRef = useRef(null);
  useEffect(() => {
    if (!werkzeugeOffen) return undefined;
    const onDown = (e) => {
      if (werkzeugeRef.current && !werkzeugeRef.current.contains(e.target)) setWerkzeugeOffen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [werkzeugeOffen]);

  // "Naechster Schritt" (07.10.2026, Ralph): Angebot -> AB / Rechnung, AB -> Rechnung.
  // Startet dieselben Dialoge wie die frueheren Knoepfe ueber dem Dokument; in der Rechnung ausgeblendet.
  const schritte = isNew || vorlageModus || !onNaechsterSchritt ? [] : type === "quote"
    ? [{ ziel: "order", label: "Auftragsbestätigung erstellen", Icon: ClipboardCheck, testid: "btn-create-order-from-quote" },
       { ziel: "invoice-from-quote", label: "Rechnung erstellen", Icon: Receipt, testid: "btn-create-invoice-from-quote" }]
    : type === "order" ? [{ ziel: "invoice-from-order", label: "Rechnung erstellen", Icon: Receipt, testid: "btn-create-invoice-from-order" }] : [];
  const [schrittOffen, setSchrittOffen] = useState(false);
  const schrittRef = useRef(null);
  useEffect(() => {
    if (!schrittOffen) return undefined;
    const onDown = (e) => {
      if (schrittRef.current && !schrittRef.current.contains(e.target)) setSchrittOffen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [schrittOffen]);

  return (
    <div className="fixed top-0 left-0 right-0 bg-card text-card-foreground border-b z-40 shadow-sm">
      <div className="lg:max-w-[1100px] lg:mx-auto flex items-center justify-between px-3 lg:px-4 py-2 lg:py-3">
        <div className="flex items-center gap-2 lg:gap-4 min-w-0">
          <Button variant="ghost" size="sm" onClick={handleBack} className="text-foreground">
            <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5" />
            <span className="hidden sm:inline">Zurück</span>
          </Button>
          <div className="h-6 w-px bg-border hidden sm:block" />
          <h1 className="text-sm lg:text-xl font-bold text-primary truncate">
            {vorlageModus ? `Vorlage ${titles[type]}` : isNew ? `${titles[type]}` : `${titles[type]} ${docNumber}`}
          </h1>
          {projektSchild && <div className="hidden 2xl:block shrink-0 max-w-[24rem]">{projektSchild}</div>}
        </div>
        <div className="flex items-center gap-1.5 lg:gap-2 shrink-0 [&_span]:text-xs">
          {/* Werkzeuge-Dropdown: Einstellungen, Vorlage oeffnen, Bausteine, Vorschau */}
          <div className="relative" ref={werkzeugeRef}>
            <Button variant="outline" size="sm" onClick={() => setWerkzeugeOffen(v => !v)} data-testid="btn-werkzeuge-topbar" className="bg-background text-foreground border-border">
              <Wrench className="w-4 h-4" />
              <span className="hidden sm:inline">Werkzeuge</span>
              <ChevronDown className={`w-3 h-3 ml-0.5 transition-transform ${werkzeugeOffen ? "rotate-180" : ""}`} />
            </Button>
            {werkzeugeOffen && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-card border rounded-sm shadow-lg min-w-[200px]" data-testid="werkzeuge-dropdown">
                <button onClick={() => { setShowSettings(true); setWerkzeugeOffen(false); }} className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2" data-testid="btn-werkzeuge-settings">
                  <Wrench className="w-4 h-4" /> Einstellungen
                </button>
                {!vorlageModus && <button onClick={() => { onOpenDocTemplates(); setWerkzeugeOffen(false); }} className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2" data-testid="btn-werkzeuge-templates">
                  <Package className="w-4 h-4" /> Vorlage öffnen
                </button>}
                <button onClick={() => { onOpenBausteine(); setWerkzeugeOffen(false); }} className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2" data-testid="btn-werkzeuge-bausteine">
                  <Bookmark className="w-4 h-4" /> Bausteine
                </button>
                <button onClick={() => { onOpenDocCheck(); setWerkzeugeOffen(false); }} className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2" data-testid="btn-werkzeuge-pruefen">
                  <FileCheck2 className="w-4 h-4" /> Prüfen
                </button>
                {/* HTML-Vorschau stillgelegt am 04.10.2026: zeigte ein anderes Datum als
                    das PDF und ihr PDF-Knopf war wirkungslos. Echte Vorschau = Knopf "Vorschau". */}
                {false && !isNew && (
                  <button onClick={() => { onTogglePreview(); setWerkzeugeOffen(false); }} className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2" data-testid="btn-werkzeuge-preview">
                    <Eye className="w-4 h-4" /> Vorschau
                  </button>
                )}
                {setZoomLevel && (
                  <>
                    <div className="border-t my-1" />
                    <div className="px-3 py-1.5">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Zoom</div>
                      <div className="flex items-center border rounded-sm overflow-hidden" data-testid="werkzeuge-zoom-group">
                        {[75, 100, 125, 150].map(z => (
                          <button
                            key={z}
                            onClick={() => setZoomLevel(z)}
                            className={`flex-1 px-2 py-1 text-xs font-medium border-l first:border-l-0 ${zoomLevel === z ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`}
                            data-testid={`btn-zoom-${z}`}
                            title={`Ansicht ${z}%`}
                          >
                            {z}%
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          {schritte.length > 0 && (
            <div className="relative" ref={schrittRef}>
              <Button variant="outline" size="sm" onClick={() => setSchrittOffen(v => !v)} data-testid="btn-naechster-schritt" title="Nächster Schritt" aria-label="Nächster Schritt" className="bg-background text-foreground border-border">
                <ArrowRightCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Nächster Schritt</span>
                <ChevronDown className={`w-3 h-3 ml-0.5 transition-transform ${schrittOffen ? "rotate-180" : ""}`} />
              </Button>
              {schrittOffen && (
                <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-50 bg-card border rounded-sm shadow-lg min-w-[230px]" data-testid="naechster-schritt-dropdown">
                  {schritte.map(({ ziel, label, Icon, testid }) => (
                    <button key={ziel} disabled={copying} onClick={() => { setSchrittOffen(false); onNaechsterSchritt(ziel); }}
                      className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2 disabled:opacity-50" data-testid={testid}>
                      <Icon className="w-4 h-4" /> {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={onToggleLeistungen} data-testid="btn-leistungen-topbar" title="Leistungen & Artikel ein-/ausblenden" className="bg-background text-foreground border-border">
            <Wrench className="w-4 h-4" />
            <span className="hidden sm:inline">Leistungen</span>
          </Button>
          <Button variant="outline" size="sm" onClick={onToggleLohnkosten} data-testid="btn-lohnkosten-topbar" title="Lohnkosten ein-/ausblenden" className="bg-background text-foreground border-border">
            <Calculator className="w-4 h-4" />
            <span className="hidden sm:inline">Lohnkosten</span>
          </Button>
          {!vorlageModus && <Button variant="outline" size="sm" onClick={onOpenPdfPreview} data-testid="btn-pdfpreview-topbar" title="PDF-Vorschau (so wird gedruckt)" className="bg-background text-foreground border-border">
            <FileSearch className="w-4 h-4" />
            <span className="hidden sm:inline">Vorschau</span>
          </Button>}
          {!vorlageModus && <Button
            variant={isRecording ? "destructive" : "outline"}
            size="sm"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={aiLoading}
            data-testid="btn-voice-input"
            className={isRecording ? "" : "bg-background text-foreground border-border"}
          >
            {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            <span className="hidden sm:inline">{isRecording ? "Stop" : "Spracheingabe"}</span>
          </Button>}
          {aiLoading && (
            <span className="text-xs text-muted-foreground hidden sm:inline">KI verarbeitet...</span>
          )}
          {false && !isNew && (
            <HelpTip id="doc.btn-pdf" placement="bottom" text="Drucken: Speichert den aktuellen Stand und öffnet den Druck-Dialog mit frischem PDF.">
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="btn-print-document" className="bg-background text-foreground border-border">
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">Drucken</span>
            </Button>
            </HelpTip>
          )}
          {/* Drucken-Knopf stillgelegt am 13.07.2026: zeigte UUID statt sprechendem
              Dateinamen beim Speichern (Blob + window.open-Einschraenkung von Chrome).
              Nutze stattdessen "Vorschau" -> Drucken/Download von dort. Code bleibt
              erhalten falls spaeter doch wieder gebraucht. */}
          {false && !isNew && (
            <HelpTip id="doc.btn-pdf" placement="bottom">
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="btn-pdf-document" className="bg-background text-foreground border-border">
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">PDF</span>
            </Button>
            </HelpTip>
          )}
          {/* PDF-Knopf stillgelegt am 13.07.2026: gleicher Grund wie Drucken-Knopf
              oben, siehe Kommentar dort. */}
        </div>
      </div>
    </div>
  );
};

export { EditorToolbar };
